import { useEffect, useRef, useState } from 'react';

/**
 * Google Identity Services 버튼.
 *
 * **ID 토큰 플로우다** (`docs/13` §1.2 — 서버가 `audience` 까지 검증한다). 그래서
 * 리디렉션 URI 도 클라이언트 시크릿도 필요 없고, 구글 콘솔에 등록할 것은 승인된
 * 자바스크립트 원본 하나뿐이다 (`BLOOM-확장-지시서.md` §8.1).
 *
 * ── 화면이 조용히 비지 않게 한다 ──────────────────────────────
 *
 * 이 경로의 실패는 전부 **버튼이 안 뜨는 것**으로만 나타난다 (`docs/11` §8).
 * 오리진 미등록·클라이언트 ID 오타·스크립트를 못 받는 상황(부스의 오프라인,
 * `docs/11` §8)이 화면에서 구분되지 않는다. 그래서 세 상태를 각각 말한다 —
 * 불러오는 중 / 못 불러옴 / 떴음.
 *
 * ── iOS 홈 화면 앱 ────────────────────────────────────────────
 *
 * 홈 화면에 추가된 앱에서 이 팝업이 결과를 돌려주지 못하는 사례가 보고돼 있다
 * (`docs/11` §0.3 — 이 데모에서 가장 위험한 한 걸음). 대응은 셋이고 그중 둘(전날
 * 로그인해 두기 · Safari 탭으로 쓰기)은 코드가 필요 없다. 세 번째(`ux_mode: 'redirect'`)가
 * 필요해지면 여기가 고칠 자리다 — **리디렉션 URI 를 구글 콘솔에 먼저 등록해야 한다.**
 */
export function GoogleButton({
  clientId,
  onCredential,
  redirect,
}: {
  clientId: string;
  /** GIS 가 준 credential(JWT). 그대로 `POST /v1/auth/google` 로 간다 */
  onCredential: (idToken: string) => void;
  /**
   * **홈 화면 앱(standalone)이면 팝업 대신 페이지를 옮긴다** (v17 R9 · `docs/40` D11).
   *
   * 그 화면에서 팝업은 **별도 브라우저 맥락**으로 열려 `postMessage` 로 돌아오지 못한다
   * (`docs/15` §412). redirect 모드는 구글이 우리 서버로 credential 을 form POST 하고,
   * 서버가 세션 토큰을 프래그먼트에 실어 앱으로 되돌린다.
   *
   * **PC·Android 의 팝업은 건드리지 않는다** (D13) — 그쪽이 실기로 증명된 유일한
   * 로그인이다(`docs/21` §12.1). 전부 옮기면 증명된 것을 미증명으로 바꾼다.
   *
   * 이 값이 있으면 `onCredential` 은 **불리지 않는다.** 결과가 이 문서로 돌아오지 않고
   * 서버를 거쳐 새 문서로 오기 때문이다.
   */
  redirect?: {
    /** 구글이 credential 을 POST 할 곳. **구글 콘솔의 승인된 리디렉션 URI 와 같아야 한다** */
    loginUri: string;
    /**
     * 함께 실어 보낼 기기 ID (v16 승격의 입력).
     *
     * **GIS 가 이 값을 돌려주는지는 아직 실기로 확인하지 않았다** (`TASKS-V17.md` R9).
     * 돌려주지 않으면 이 화면에서만 승격이 일어나지 않고, 증상은 「로그인했더니 이어보기가
     * 처음으로 돌아갔다」다 — **로그인 자체는 그대로 성공한다.**
     */
    state?: string;
  };
}) {
  const host = useRef<HTMLDivElement | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  // 콜백이 최신 것을 보게 한다. GIS 는 `initialize()` 시점의 함수를 붙들고 있으므로
  // 그대로 두면 첫 렌더의 클로저가 영원히 남는다.
  const latest = useRef(onCredential);
  latest.current = onCredential;

  useEffect(() => {
    let alive = true;

    void loadGis()
      .then((gis) => {
        if (!alive || host.current === null) return;
        gis.initialize(
          redirect === undefined
            ? {
                client_id: clientId,
                callback: (response) => {
                  latest.current(response.credential);
                },
              }
            : {
                client_id: clientId,
                ux_mode: 'redirect',
                login_uri: redirect.loginUri,
                ...(redirect.state === undefined ? {} : { state: redirect.state }),
              },
        );
        gis.renderButton(host.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          locale: 'ko',
        });
        setState('ready');
      })
      .catch(() => {
        if (alive) setState('error');
      });

    return () => {
      alive = false;
    };
  }, [clientId, redirect?.loginUri, redirect?.state]);

  return (
    <div>
      <div ref={host} />
      {state === 'loading' && <p className="card__text">구글 로그인을 불러오는 중…</p>}
      {state === 'error' && (
        <p className="card__text">
          구글 로그인을 불러오지 못했습니다. 인터넷 연결을 확인해 주세요 — 로그인은 구글 서버를
          거칩니다.
        </p>
      )}
    </div>
  );
}

const GIS_SRC = 'https://accounts.google.com/gsi/client';

/**
 * GIS 스크립트를 한 번만 싣는다.
 *
 * **모듈 스코프 약속이다** — 두 번 실으면 `google.accounts.id` 가 두 번 초기화되고,
 * 화면이 리마운트될 때마다 스크립트 태그가 쌓인다 (`useTv2m` 의 단일 비행과 같은 방침).
 */
let gisPromise: Promise<GoogleIdentityApi> | null = null;

function loadGis(): Promise<GoogleIdentityApi> {
  gisPromise ??= new Promise<GoogleIdentityApi>((resolve, reject) => {
    const existing = window.google?.accounts.id;
    if (existing) {
      resolve(existing);
      return;
    }

    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.onload = () => {
      const api = window.google?.accounts.id;
      if (api) resolve(api);
      else reject(new Error('GIS 를 실었는데 google.accounts.id 가 없다.'));
    };
    // **오프라인에서 매달리지 않게 한다.** 스크립트를 못 받으면 `onerror` 가 오고,
    // 그때 화면이 이유를 말한다 (`docs/11` §8 이 지목한 자리).
    script.onerror = () => {
      gisPromise = null;
      reject(new Error('GIS 스크립트를 받지 못했다.'));
    };
    document.head.append(script);
  });
  return gisPromise;
}

/** GIS 의 표면 중 우리가 쓰는 것만. 공식 타입 패키지를 새 의존성으로 들이지 않는다. */
interface GoogleIdentityApi {
  /**
   * 팝업(기본)과 redirect 두 모양을 받는다.
   *
   * **redirect 에는 `callback` 이 없다** — 결과가 이 문서로 돌아오지 않고 구글이 우리
   * 서버로 POST 한다. 유니온으로 둔 이유가 그것이다: 한 객체에 섞으면 「callback 을 줬는데
   * 안 불린다」가 컴파일을 통과한다.
   */
  initialize(
    options:
      | {
          client_id: string;
          callback: (response: { credential: string }) => void;
        }
      | {
          client_id: string;
          ux_mode: 'redirect';
          login_uri: string;
          state?: string;
        },
  ): void;
  renderButton(
    parent: HTMLElement,
    options: {
      type: 'standard' | 'icon';
      theme?: 'outline' | 'filled_blue' | 'filled_black';
      size?: 'small' | 'medium' | 'large';
      text?: 'signin_with' | 'signup_with' | 'continue_with';
      locale?: string;
    },
  ): void;
}

declare global {
  interface Window {
    google?: { accounts: { id: GoogleIdentityApi } };
  }
}
